import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

import type {
  BdAbteilungsart,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  BD_ABTEILUNGSART_DISPLAY_NAMES
} from '@bindoc-projects/virtual-clinic-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
  FilterDetailedText,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

export const BD_ABTEILUNGSART_CONDITION_ID = 'abteilungsart';
export const FilterConditionAbteilungsartPluginType: FilterConditionPluginSpecification<
  BdAbteilungsart[],
  FilterConditionAbteilungsartDto,
  BdAbteilungsartCondition
> = {
  type: 'abteilungsart',
  category: 'case',
  displayName: { de: 'Abteilungsart', en: 'Specialty department type' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionAbteilungsartDto,
  ): FilterConditionPluginImpl<BdAbteilungsart[]> {
    return new FilterConditionAbteilungsartPlugin(obj.abteilungsart);
  },
  fromCondition(
    jsonObj: BdAbteilungsartCondition,
  ): FilterConditionPluginImpl<BdAbteilungsart[]> {
    return new FilterConditionAbteilungsartPlugin(
      (jsonObj.includes ?? []) as BdAbteilungsart[],
    );
  },
  newInstance(): FilterConditionPluginImpl<BdAbteilungsart[]> {
    return new FilterConditionAbteilungsartPlugin([]);
  },
};

export class FilterConditionAbteilungsartPlugin extends FilterConditionPluginImpl<
  BdAbteilungsart[],
  FilterConditionAbteilungsartDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    BdAbteilungsart[],
    FilterConditionAbteilungsartDto,
    BdAbteilungsartCondition
  > = FilterConditionAbteilungsartPluginType;

  constructor(private abteilungsart: BdAbteilungsart[]) {
    super(FilterConditionAbteilungsartPluginType.type);
  }

  clone(): FilterConditionAbteilungsartPlugin {
    return new FilterConditionAbteilungsartPlugin([...this.abteilungsart]);
  }

  override equals(other: FilterConditionAbteilungsartPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.abteilungsart, other.abteilungsart)
    );
  }

  override validationToText(): string {
    if (this.abteilungsart.length === 0) {
      return 'Mindestens eine Abteilungsart auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdAbteilungsartCondition(this.abteilungsart, []);
  }

  toJSON(): FilterConditionAbteilungsartDto | null {
    return {
      type: this.type,
      abteilungsart: this.abteilungsart,
    };
  }

  toText(): string | null {
    return this.abteilungsart.length === 1
      ? 'eine Abteilungsart'
      : `${this.abteilungsart.length} Abteilungsarten`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.abteilungsart.map(
      (a) => BD_ABTEILUNGSART_DISPLAY_NAMES[a] ?? a,
    );
    return { title, items };
  }

  toValue(): BdAbteilungsart[] {
    return this.abteilungsart;
  }
}

export class BdAbteilungsartCondition extends BdContentCondition<BdAbteilungsart> {
  constructor(includes: BdAbteilungsart[], excludes: BdAbteilungsart[]) {
    super(BD_ABTEILUNGSART_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionAbteilungsartDto {
  type: string;
  abteilungsart: BdAbteilungsart[];
}
